import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FunnelIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  ArrowsUpDownIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const MyTasks = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [sortBy, setSortBy] = useState('dueDate');
  const [viewMode, setViewMode] = useState('list');
  const [searchQuery, setSearchQuery] = useState('');

  // Comprehensive tasks data
  const tasks = [
    {
      id: 1,
      title: "Complete Annual Health Assessment",
      assignedBy: "Dr. Meera Gupta",
      dueDate: "2025-07-25",
      priority: "High",
      status: "In Progress",
      description: "Annual mandatory health screening and assessment",
      project: "Healthcare Compliance",
      progress: 65,
      tags: ["Health", "Mandatory", "Annual"],
      estimatedHours: 4,
      actualHours: 2.5,
      category: "Health & Safety",
      department: "Medical",
      createdDate: "2025-07-20",
    },
    {
      id: 2,
      title: "Review New Medical Protocol Guidelines",
      assignedBy: "Medical Director",
      dueDate: "2025-07-30",
      priority: "Medium",
      status: "Pending",
      description: "Review and acknowledge updated treatment protocols",
      project: "Protocol Updates",
      progress: 0,
      tags: ["Protocol", "Review", "Medical"],
      estimatedHours: 2,
      actualHours: 0,
      category: "Training",
      department: "Medical",
      createdDate: "2025-07-22",
    },
    {
      id: 3,
      title: "Submit Monthly Quality Metrics",
      assignedBy: "Quality Assurance Team",
      dueDate: "2025-08-01",
      priority: "High",
      status: "Not Started",
      description: "Compile and submit departmental quality metrics",
      project: "Quality Management",
      progress: 0,
      tags: ["Quality", "Metrics", "Monthly"],
      estimatedHours: 3,
      actualHours: 0,
      category: "Reporting",
      department: "Quality",
      createdDate: "2025-07-23",
    },
    {
      id: 4,
      title: "Equipment Maintenance Checklist",
      assignedBy: "Facilities Manager",
      dueDate: "2025-07-26",
      priority: "Medium",
      status: "In Progress",
      description: "Complete weekly equipment maintenance checks",
      project: "Facilities Management",
      progress: 40,
      tags: ["Equipment", "Maintenance", "Weekly"],
      estimatedHours: 2.5,
      actualHours: 1,
      category: "Maintenance",
      department: "Facilities",
      createdDate: "2025-07-21",
    },
    {
      id: 5,
      title: "Patient Satisfaction Survey Analysis",
      assignedBy: "Quality Director",
      dueDate: "2025-07-28",
      priority: "Low",
      status: "Completed",
      description: "Analyze Q2 patient satisfaction survey results",
      project: "Patient Experience",
      progress: 100,
      tags: ["Survey", "Analysis", "Patient"],
      estimatedHours: 6,
      actualHours: 5.5,
      category: "Analysis",
      department: "Quality",
      createdDate: "2025-07-15",
    },
    {
      id: 6,
      title: "Staff Training Session Preparation",
      assignedBy: "Training Coordinator",
      dueDate: "2025-08-05",
      priority: "Medium",
      status: "Pending",
      description: "Prepare materials for upcoming safety training session",
      project: "Staff Development",
      progress: 15,
      tags: ["Training", "Safety", "Preparation"],
      estimatedHours: 4,
      actualHours: 0.5,
      category: "Training",
      department: "HR",
      createdDate: "2025-07-24",
    },
    {
      id: 7,
      title: "Update Emergency Response Procedures",
      assignedBy: "Safety Officer",
      dueDate: "2025-07-29",
      priority: "High",
      status: "In Progress",
      description: "Review and update emergency response documentation",
      project: "Safety Compliance",
      progress: 75,
      tags: ["Emergency", "Safety", "Procedures"],
      estimatedHours: 5,
      actualHours: 3.5,
      category: "Safety",
      department: "Safety",
      createdDate: "2025-07-18",
    },
    {
      id: 8,
      title: "Inventory Management System Update",
      assignedBy: "Inventory Manager",
      dueDate: "2025-08-10",
      priority: "Low",
      status: "Not Started",
      description: "Update inventory tracking for medical supplies",
      project: "Supply Chain",
      progress: 0,
      tags: ["Inventory", "System", "Supplies"],
      estimatedHours: 3,
      actualHours: 0,
      category: "Administration",
      department: "Supply Chain",
      createdDate: "2025-07-25",
    },
  ];

  // Filter and sort tasks
  const filteredTasks = tasks
    .filter(task => {
      const matchesStatus = selectedFilter === 'all' || 
        task.status.toLowerCase().replace(' ', '') === selectedFilter;
      const matchesPriority = selectedPriority === 'all' || 
        task.priority.toLowerCase() === selectedPriority;
      const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.project.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.assignedBy.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesStatus && matchesPriority && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'dueDate':
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'priority':
          const priorityOrder = { 'High': 3, 'Medium': 2, 'Low': 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'progress':
          return b.progress - a.progress;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'created':
          return new Date(b.createdDate) - new Date(a.createdDate);
        default:
          return 0;
      }
    });

  // Statistics
  const stats = {
    total: tasks.length,
    inProgress: tasks.filter(t => t.status === 'In Progress').length,
    pending: tasks.filter(t => t.status === 'Pending').length,
    completed: tasks.filter(t => t.status === 'Completed').length,
    overdue: tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'Completed').length,
  };

  // Helper functions
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-50 border-green-200';
      case 'In Progress': return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'Pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Not Started': return 'text-gray-600 bg-gray-50 border-gray-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const isOverdue = (dueDate, status) => {
    return new Date(dueDate) < new Date().setHours(0, 0, 0, 0) && status !== 'Completed';
  };

  const isDueToday = (dueDate) => {
    const today = new Date().toDateString();
    return new Date(dueDate).toDateString() === today;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
              <p className="text-gray-600 mt-1">
                View and manage all your assigned tasks
              </p>
            </div>
            <Link
              to="/tasks/new"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>New Task</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Tasks</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="text-2xl font-bold text-blue-600">{stats.inProgress}</div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6 border">
            <div className="text-2xl font-bold text-red-600">{stats.overdue}</div>
            <div className="text-sm text-gray-600">Overdue</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6 border-b">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
                {/* Search */}
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
                  />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="inprogress">In Progress</option>
                    <option value="pending">Pending</option>
                    <option value="notstarted">Not Started</option>
                    <option value="completed">Completed</option>
                  </select>
                  <FunnelIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                {/* Priority Filter */}
                <div className="relative">
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Priorities</option>
                    <option value="high">High Priority</option>
                    <option value="medium">Medium Priority</option>
                    <option value="low">Low Priority</option>
                  </select>
                  <ExclamationTriangleIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                {/* Sort */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="dueDate">Sort by Due Date</option>
                    <option value="priority">Sort by Priority</option>
                    <option value="progress">Sort by Progress</option>
                    <option value="title">Sort by Title</option>
                    <option value="created">Sort by Created</option>
                  </select>
                  <ArrowsUpDownIcon className="h-4 w-4 absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>

                {/* View Mode Toggle */}
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      viewMode === 'list'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    List
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Grid
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="px-6 py-3 bg-gray-50 border-b">
            <p className="text-sm text-gray-600">
              Showing {filteredTasks.length} of {tasks.length} tasks
            </p>
          </div>
        </div>

        {/* Tasks Content */}
        {filteredTasks.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
            <CheckCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-500 mb-6">
              Try adjusting your filters or search criteria to find tasks.
            </p>
            <Link
              to="/tasks/new"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create New Task
            </Link>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {filteredTasks.map((task) => (
              <div
                key={task.id}
                className={`bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow ${
                  viewMode === 'list' ? 'p-6' : 'p-4'
                }`}
              >
                {viewMode === 'list' ? (
                  // List View
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <Link
                          to={`/tasks/${task.id}`}
                          className="text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors"
                        >
                          {task.title}
                        </Link>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        {isOverdue(task.dueDate, task.status) && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full text-red-600 bg-red-50 border border-red-200">
                            Overdue
                          </span>
                        )}
                        {isDueToday(task.dueDate) && (
                          <span className="px-2 py-1 text-xs font-medium rounded-full text-orange-600 bg-orange-50 border border-orange-200">
                            Due Today
                          </span>
                        )}
                      </div>
                      
                      <p className="text-gray-600 mb-3">{task.description}</p>
                      
                      <div className="flex items-center space-x-6 text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span>{task.assignedBy}</span>
                        </div>
                        <div className="flex items-center">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          <span>Due {formatDate(task.dueDate)}</span>
                        </div>
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          <span>{task.actualHours}h / {task.estimatedHours}h</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">Project:</span>
                          <Link
                            to={`/projects/${task.project.toLowerCase().replace(' ', '-')}`}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            {task.project}
                          </Link>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500">{task.progress}%</span>
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${task.progress}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-4 flex items-center space-x-2">
                      <Link
                        to={`/tasks/${task.id}/edit`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      <Link
                        to={`/tasks/${task.id}`}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  // Grid View
                  <div>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Link
                          to={`/tasks/${task.id}/edit`}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <PencilIcon className="h-3 w-3" />
                        </Link>
                        <Link
                          to={`/tasks/${task.id}`}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <EyeIcon className="h-3 w-3" />
                        </Link>
                      </div>
                    </div>
                    
                    <Link
                      to={`/tasks/${task.id}`}
                      className="block text-lg font-medium text-gray-900 hover:text-blue-600 transition-colors mb-2"
                    >
                      {task.title}
                    </Link>
                    
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{task.description}</p>
                    
                    <div className="space-y-2 text-xs text-gray-500 mb-4">
                      <div className="flex items-center">
                        <UserIcon className="h-3 w-3 mr-1" />
                        <span>{task.assignedBy}</span>
                      </div>
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-3 w-3 mr-1" />
                        <span>Due {formatDate(task.dueDate)}</span>
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-3 w-3 mr-1" />
                        <span>{task.actualHours}h / {task.estimatedHours}h</span>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                        <span>Progress</span>
                        <span>{task.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${task.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Link
                        to={`/projects/${task.project.toLowerCase().replace(' ', '-')}`}
                        className="text-xs text-blue-600 hover:text-blue-700 truncate"
                      >
                        {task.project}
                      </Link>
                      <div className="flex items-center space-x-1">
                        {task.tags.slice(0, 2).map((tag, index) => (
                          <span
                            key={index}
                            className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {(isOverdue(task.dueDate, task.status) || isDueToday(task.dueDate)) && (
                      <div className="mt-2 pt-2 border-t">
                        {isOverdue(task.dueDate, task.status) && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full text-red-600 bg-red-50 border border-red-200">
                            <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                            Overdue
                          </span>
                        )}
                        {isDueToday(task.dueDate) && (
                          <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full text-orange-600 bg-orange-50 border border-orange-200">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            Due Today
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default MyTasks;
